import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { listSourceGeneALL, listSourceVariantAll_VDAs } from '../../search/searchGrid/helpers/tableHelpers';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { trackEnrichmentAction, trackEvent } from '../../../utils/analytics';
import SharedButton from '@/global/Button/Button';
import { useNavigate } from 'react-router-dom';
import { useEnrichmentState } from '../../../hooks/useEnrichmentState';
import { APP_MODAL_Z_INDEX } from '@/constants/modalZIndex';
import {
  DEFAULT_ENRICHMENT_ORDER,
  DEFAULT_ENRICHMENT_ORDER_BY,
  getAdvancedSearchFilterForUnresolvedGenes,
  getEnrichmentScoreMax,
} from '../helpers';
import { getMeshDiseaseClassOptions, normalizeMeshDiseaseClassName } from '../meshDiseaseClasses';
import MultiSelectDropdown from '@/components/Academic/MultiSelectDropdown';

const EnrichmentModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialParams,
  searchType,
  notFoundItems,
  user,
  currentText,
  geneFilterType = 'geneSymbol',
}) => {
  const [params, setParams] = useState(initialParams);
  const navigate = useNavigate();
  const { setPendingAdvancedSearchRedirect } = useEnrichmentState();

  const userGroups = user?.['cognito:groups'];
  const isAcademic = Array.isArray(userGroups) && userGroups.includes('ROLE_ACADEMIC');

  useEffect(() => {
    if (isOpen) {
      const adjustedParams = { ...initialParams };

      if (searchType === 'GENES' && adjustedParams.source === 'ALL') {
        adjustedParams.source = 'CURATED';
      }

      if (isAcademic && adjustedParams.source === 'ALL') {
        adjustedParams.source = 'CURATED';
      }

      if (!adjustedParams.commonGenes) {
        adjustedParams.commonGenes = adjustedParams.commonVariants || 2;
      }
      if (!adjustedParams.commonVariants) {
        adjustedParams.commonVariants = adjustedParams.commonGenes || 2;
      }

      setParams(adjustedParams);
    }
  }, [initialParams, isOpen, isAcademic, searchType]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Filter sources based on user role
  const getFilteredSources = () => {
    const sourceList = searchType === 'GENES' ? listSourceGeneALL : listSourceVariantAll_VDAs;

    // If user is academic, only show CURATED
    if (isAcademic) {
      const filtered = {};
      Object.entries(sourceList).forEach(([group, options]) => {
        if (group === 'CURATED') {
          filtered[group] = options;
        }
      });
      return filtered;
    }

    // For other users, show all sources
    return sourceList;
  };

  const filteredSources = getFilteredSources();
  const scoreMax = getEnrichmentScoreMax(searchType);
  const meshClassOptions = getMeshDiseaseClassOptions(searchType).map(className => ({
    value: className,
    label: className,
  }));
  const selectedMeshClasses: string[] = Array.isArray(params.diseaseClassName_MSH)
    ? params.diseaseClassName_MSH.map(normalizeMeshDiseaseClassName).filter(Boolean)
    : [];

  const handleInputChange = (field, value) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  const handleMeshClassChange = (values: string[]) => {
    setParams(prev => ({
      ...prev,
      diseaseClassName_MSH: values.map(normalizeMeshDiseaseClassName).filter(Boolean),
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    trackEnrichmentAction('run_enrichment_submit', 'enrichment_modal');
    onSubmit(params);
  };

  const handleReset = () => {
    setParams({
      commonVariants: 2,
      commonGenes: 2,
      maxPvalue: 0.05,
      minScore: 0.1,
      pageNumber: 0,
      source: 'CURATED',
      orderBy: DEFAULT_ENRICHMENT_ORDER_BY,
      order: DEFAULT_ENRICHMENT_ORDER,
      diseaseClassName_MSH: [],
    });
  };

  const handleAdvancedSearchRedirect = () => {
    const unresolvedSymbols = notFoundItems
      .map((item: string) => item.trim())
      .filter((item: string) => item.length > 0)
      .join('\n');

    setPendingAdvancedSearchRedirect({
      searchType: 'GENES',
      selectedFilter: getAdvancedSearchFilterForUnresolvedGenes(notFoundItems, geneFilterType),
      text: unresolvedSymbols,
      autoSearch: true,
      originalRequestText: currentText,
      unresolvedItems: notFoundItems,
    });
    navigate('/advancedSearch');
    onClose();
  };

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 overflow-y-auto bg-black bg-opacity-50"
      style={{ zIndex: APP_MODAL_Z_INDEX }}
    >
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col overflow-y-auto"
          data-demo-tour="enrichment-modal-root"
        >
          <div className="flex-shrink-0 flex justify-between items-center p-6 pb-4">
            <h2 className="text-xl font-bold text-gray-900">Enrichment Parameters</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-demo-tour="enrichment-modal-close"
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              <XMarkIcon className="h-5 w-5 text-black" />
            </button>
          </div>

          <div className="px-6 pb-6">
            {notFoundItems.length > 0 && (
              <>
                <div className="warning-message p-2 text-center block w-full max-h-40 overflow-y-auto">
                  <span>
                    These IDs are not found:{' '}
                    <strong className="break-words">{notFoundItems.join(', ')}</strong>
                  </span>
                </div>
                {searchType === 'GENES' && (
                  <p className="mt-2 text-xs text-center">
                    You can find these official gene symbols in{' '}
                    <button
                      type="button"
                      onClick={handleAdvancedSearchRedirect}
                      className="underline font-semibold hover:opacity-80 text-xs text-pink"
                    >
                      Advanced Search
                    </button>
                    .
                  </p>
                )}
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {searchType === 'GENES' ? 'Common Genes' : 'Common Variants'}
                </label>
                <input
                  type="number"
                  value={searchType === 'GENES' ? params.commonGenes : params.commonVariants}
                  onChange={e => {
                    const fieldName = searchType === 'GENES' ? 'commonGenes' : 'commonVariants';
                    handleInputChange(fieldName, parseInt(e.target.value, 10) || 0);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum number of common {searchType === 'GENES' ? 'genes' : 'variants'} required
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max P-value</label>
                <input
                  type="number"
                  step="any"
                  value={params.maxPvalue}
                  onChange={e => handleInputChange('maxPvalue', parseFloat(e.target.value) || 0)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min="0"
                  max="1"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum p-value threshold (0-1)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Score</label>
                <input
                  type="number"
                  step="0.1"
                  value={params.minScore}
                  onChange={e => {
                    const raw = parseFloat(e.target.value) ?? 0;
                    const clamped = Math.max(0, Math.min(raw, scoreMax));
                    handleInputChange('minScore', clamped);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min="0"
                  max={scoreMax}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum DISGENET score threshold (0–{scoreMax})</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>

                <select
                  value={params.source}
                  onChange={e => handleInputChange('source', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(filteredSources).map(([group, options]) => (
                    <optgroup key={group} label={group}>
                      {(options as string[]).map(opt => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                <p className="text-xs text-gray-500 mt-1">Data source for enrichment analysis</p>
              </div>

              <div className="relative z-30">
                <MultiSelectDropdown
                  label="MeSH disease class"
                  options={meshClassOptions}
                  value={selectedMeshClasses}
                  onChange={handleMeshClassChange}
                  placeholder="All classes"
                  theme="pink"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional. Leave empty to include all classes. Multiple classes allowed.
                </p>
              </div>

              <div className="flex pt-4">
                <SharedButton
                  id="enrichment_run"
                  variant="gradient"
                  type="submit"
                  className="filter-upload"
                  data-demo-tour="enrichment-modal-submit"
                >
                  Run Enrichment
                </SharedButton>                <SharedButton
                  id="enrichment_reset"
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    trackEvent({
                      name: 'form_reset',
                      buttonId: 'enrichment_modal_reset',
                      label: 'Reset enrichment modal',
                      page: 'enrichment',
                      section: 'enrichment_modal',
                      feature: 'enrichment',
                      eventCategory: 'enrichment',
                    });
                    handleReset();
                  }}
                  className="filter-upload"
                >
                  Reset
                </SharedButton>
                <SharedButton
                  id="enrichment_cancel"
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    trackEvent({
                      name: 'modal_close',
                      buttonId: 'enrichment_modal_cancel',
                      label: 'Cancel enrichment modal',
                      page: 'enrichment',
                      section: 'enrichment_modal',
                      feature: 'enrichment',
                      eventCategory: 'enrichment',
                    });
                    onClose();
                  }}
                  className="filter-upload"
                >
                  Cancel
                </SharedButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EnrichmentModal;
